import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconCSV } from 'src/assets/icons/IconCSV';
import { IconDelete } from 'src/assets/icons/IconDelete';
import { IconUpload } from 'src/assets/icons/Upload';
import { getFileExtension } from 'src/lib/file';
import { formatFileSize } from 'src/lib/format';

import { DEFAULT_MAX_SIZE } from './const';
import { CsvUploadSkeleton } from './CsvUploadSkeleton';
import { useStyles } from './useStyles';

export const CsvUpload = ({
  csvFile,
  onDrop,
  onDelete,
  isLoading,
  hideEmptyState,
  displayOnly,
}) => {
  const styles = useStyles();

  const handleDrop = useCallback(
    (acceptedFiles) => {
      onDrop(acceptedFiles[0]);
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: 'text/csv',
    maxFiles: 1,
    noClick: false,
    noKeyboard: true,
    maxSize: DEFAULT_MAX_SIZE,
  });

  if (isLoading) {
    return <CsvUploadSkeleton />;
  }

  if (hideEmptyState && !csvFile) {
    return null;
  }

  return (
    <>
      {!csvFile ? (
        <div
          {...getRootProps({
            className: cx(styles.dropzone, {
              [styles.activeDropzone]: isDragActive,
            }),
          })}
        >
          <input {...getInputProps()} />
          <IconCSV className={styles.csvIcon} />
          <div className={styles.uploadButton}>
            <IconUpload className={styles.uploadIcon} />
            <button type="button">Upload CSV</button>
          </div>
        </div>
      ) : (
        <div
          className={cx(styles.uploadedFile, {
            [styles.displayOnly]: displayOnly,
          })}
        >
          <div className={styles.leftSide}>
            <IconCSV />
            <div className={styles.uploadedFileContent}>
              <div className={styles.fileName}>{csvFile?.name}</div>
              <div className={styles.uploadedFileDetails}>
                <div>{getFileExtension(csvFile?.name).toUpperCase()}</div>
                <div className={styles.divider}>|</div>
                <div>{formatFileSize(csvFile)}</div>
              </div>
            </div>
          </div>
          {!displayOnly && (
            <div className={styles.deleteIcon}>
              <IconDelete onClick={onDelete} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

CsvUpload.propTypes = {
  csvFile: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
  }),
  onDelete: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hideEmptyState: PropTypes.bool,
  displayOnly: PropTypes.bool,
};

CsvUpload.defaultProps = {
  csvFile: null,
  isLoading: false,
  hideEmptyState: false,
  displayOnly: false,
};
